import React, {ReactElement} from 'react';
import styled, {css} from 'styled-components';

import {intersperseNodes} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {findUniqueChannelType} from '../../../../helpers/models/channelHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {ChannelTypesEnum} from '../../../../models/api/channelCommonTypes';
import {ChannelModel} from '../../../../models/api/channelModel';
import {
  findWarningsForChannel,
  getWarningProps,
} from '../../../../settings/inboxes/settingsChannelWarningHelpers';
import {getApplicationChannelTypes} from '../../../../store/data/applicationChannelTypes/applicationChannelTypesSelectors';
import {getCompanyPreferences} from '../../../../store/data/company/companySelectors';
import {getExternalChannelTypes} from '../../../../store/data/externalChannelTypes/externalChannelTypesSelectors';
import {getIsUserGlobalAdmin, getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {ChannelIconSizesEnum} from '../../channelIcon';
import {ChannelIconOrImage} from '../../channelIconOrImage';
import {getCellBackgroundColor, getCellIndexStyles} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Style.
 */

const StyledIconCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  flex-flow: row;
  height: 100%;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
      false,
      p.$isOnlyColumn,
    )}
`;

const StyledIconContainerDiv = styled.div`
  flex: 0 0 40px;
  margin-right: 12px;
`;

interface TagLabelStyleProps {
  $hasSubtitle?: boolean;
}
const StyledLabelDiv = styled.div<TagLabelStyleProps>`
  overflow: hidden;

  ${(p) => addLabelStyles(Boolean(p.$hasSubtitle))};
`;

function addLabelStyles(hasSubtitle: boolean) {
  if (hasSubtitle) {
    return css`
      display: grid;
      grid-row-gap: 4px;
      grid-auto-flow: row;
      grid-auto-rows: auto;
      align-items: center;
    `;
  }

  return css`
    ${flexCenter};
  `;
}

interface SelectedStyleProps {
  $isSelected?: boolean;
}

const StyledNameDiv = styled.div<SelectedStyleProps>`
  color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.shade90)};
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.medium};
  ${ellipsis()};
  display: flex;
  gap: 8px;
`;

const StyledSubtitleDiv = styled.div<SelectedStyleProps>`
  color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.shade70)};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  display: flex;
  min-width: 0;
`;

const StyledSubtitleContentDiv = styled.div`
  ${ellipsis()}
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const ChannelCell = (props: TableCellProps<ChannelModel>): ReactElement => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;
  const externalChannelTypes = useAppSelector(getExternalChannelTypes);
  const applicationChannelTypes = useAppSelector(getApplicationChannelTypes);
  const channelUniqueType = props.value && findUniqueChannelType(props.value, externalChannelTypes);
  const applicationChannelType =
    (props.value?.channelTypeUid && applicationChannelTypes[props.value.channelTypeUid]) || undefined;
  const userNamespace = useAppSelector(getUserNamespace);
  const companyPreferences = useAppSelector(getCompanyPreferences);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const channelWarnings =
    (props.value && findWarningsForChannel(props.value, userNamespace, companyPreferences)) || [];
  const channelWarningProps = channelWarnings.map(
    (warning) => [warning, getWarningProps(warning, isUserGlobalAdmin)] as const,
  );
  const theme = useUserTheme();

  return (
    <TableCell<ChannelModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={(channel) => (
        <StyledIconCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
          $isOnlyColumn
        >
          <StyledIconContainerDiv>
            <ChannelIconOrImage
              imgSrc={applicationChannelType?.iconUrl}
              type={!applicationChannelType ? channelUniqueType || ChannelTypesEnum.CUSTOM : undefined}
              size={ChannelIconSizesEnum.BIG}
            />
          </StyledIconContainerDiv>
          <StyledLabelDiv $hasSubtitle>
            <StyledNameDiv>
              {channelWarningProps.length > 0 && (
                <TooltipCoordinator
                  render={() =>
                    intersperseNodes(
                      channelWarningProps.map(([warning, warningProps]) => ({
                        node: warningProps.label,
                        key: warning,
                      })),
                      () => <br />,
                    )
                  }
                >
                  <Icon name="warningFilledSmall" color={theme.palette.orange.shade40} />
                </TooltipCoordinator>
              )}
              {channel.displayName}
            </StyledNameDiv>
            <StyledSubtitleDiv $isSelected={props.isSelected}>
              <StyledSubtitleContentDiv>{channel.type}</StyledSubtitleContentDiv>
            </StyledSubtitleDiv>
          </StyledLabelDiv>
        </StyledIconCellContainerDiv>
      )}
    />
  );
};
